import React, { useState, useEffect, useContext } from 'react'
import styled, { keyframes } from 'styled-components'
import { Button } from 'ui/acro'
import { axios } from 'api'
import LoadingButton from '../../components_v1/loadingButton/LoadingButton'
import { camelToSnake } from 'utils'
import { dispatchProgramAction } from './ProgramButtonActions'
import { ProgramContext } from 'components_v2/page_context'

const slideUp = keyframes`
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
`

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: ${(props) => (props.isMobile ? 'flex-end' : 'center')};
  z-index: 999;

  opacity: ${(props) => (props.isOpen ? 1 : 0)};
  pointer-events: ${(props) => (props.isOpen ? 'auto' : 'none')};
  transform: ${(props) => (props.isOpen ? 'translateY(0)' : 'translateY(8px)')};
  transition: opacity 0.25s ease-out, transform 0.25s ease-out;
`

const Modal = styled.div`
  background: white;
  border-radius: 0px;
  max-width: 496px;
  width: 100%;
  position: relative;
`

const Drawer = styled.div`
  background: white;
  border-radius: 12px 12px 0 0;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  animation: ${slideUp} 0.3s ease-out;
`
const Title = styled.div`
  font-family: 'Proxima-Nova';
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.352px;
  text-align: left;
`

const TitleWrapper = styled.div`
  position: relative;
  padding: 38px 16px 0 16px;

  @media (max-width: 600px) {
    padding: 38px 12px 0 12px;
  }
`

const SubTitle = styled.div`
  font-family: 'Proxima-Nova';
  font-size: 21px;
  font-style: normal;
  font-weight: 600;
  line-height: 111%;
  margin-top: 36px;
  text-align: left;
`

const Description = styled.div`
  color: #3b3b3b;
  font-family: 'Proxima-Nova';
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.352px;
  margin-top: 12px;
  text-align: left;
`
const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 32px;
`

const CloseButton = styled.button`
  position: ${(props) => (props.isMobile ? 'static' : 'absolute')};
  top: ${(props) => (props.isMobile ? 'auto' : '38px')};
  right: ${(props) => (props.isMobile ? 'auto' : '16px')};
  background: none;
  border: none;
  font-size: 20px;
  font-weight: 400;
  margin-bottom: ${(props) => (props.isMobile ? '8px' : '0')};
  cursor: pointer;
`

const ChildWrapper = styled.div`
  padding: 0 16px 38px 16px;

  @media (max-width: 600px) {
    padding: 0 12px 38px 12px;
  }
`

const Close = styled(Button)`
  color: #000;
  width: 100%;
  max-width: 215px;
  background: #fff;
  font-family: 'Proxima-Nova';
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  border: 1px solid #000;
  padding: 12px 32px;
  border-radius: 0;
  margin-bottom: 17px;

  @media (max-width: 600px) {
    font-size: 16px;
    font-weight: 400;
    letter-spacing: 0.8px;
    padding: 12px 16px;
  }
`

const ResponsiveModalDrawer = ({ isOpen, onClose, children }) => {
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 600)
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  return (
    <Overlay onClick={onClose} isOpen={isOpen} isMobile={isMobile}>
      {isMobile ? (
        <Drawer onClick={(e) => e.stopPropagation()}>
          <TitleWrapper>
            <Title>Stop Program</Title>
            <CloseButton onClick={onClose}>✕</CloseButton>
          </TitleWrapper>
          {children}
        </Drawer>
      ) : (
        <Modal onClick={(e) => e.stopPropagation()}>
          <TitleWrapper>
            <Title>Stop Program</Title>
            <CloseButton onClick={onClose}>✕</CloseButton>
          </TitleWrapper>
          {children}
        </Modal>
      )}
    </Overlay>
  )
}

export function StopConfirmationModal({
  id,
  showStopConfirmation,
  setStopConfirmation
}) {
  const [isLoading, setLoading] = useState(false)
  const { setProgramStatus = () => {} } = useContext(ProgramContext)

  const stopProgram = async () => {
    setLoading(true)
    try {
      const res = await axios.post(`programs/${id}/stop`)
      if (res?.data?.ga4Event) {
        window.dataLayer ||= []
        window.dataLayer.push(camelToSnake(res.data?.ga4Event))
      }
      if (res.status === 200) {
      }
      setProgramStatus('stopped')
      dispatchProgramAction('stop')
      setLoading(false)
    } catch (e) {
      console.error(e)
    }
  }

  return (
    <ResponsiveModalDrawer
      isOpen={showStopConfirmation}
      onClose={() => setStopConfirmation(false)}
    >
      <ChildWrapper>
        <SubTitle>Are you sure you want to stop this program?</SubTitle>
        <Description>
          Any classes you have completed with this program will display on the
          Completed tab in My Practice, but the program progress will be reset.
        </Description>
        <ButtonWrapper>
          <LoadingButton
            onClick={stopProgram}
            style={{
              marginTop: '0',
              marginBottom: '16px',
              maxWidth: '215px',
              fontSize: '16px'
            }}
            isLoading={isLoading}
          >
            {isLoading ? '' : 'Stop Program'}
          </LoadingButton>
          <Close onClick={() => setStopConfirmation(false)}>Cancel</Close>
        </ButtonWrapper>
      </ChildWrapper>
    </ResponsiveModalDrawer>
  )
}
